// filterStorageUtils.js
import { advanceFilterData, advanceFilterList } from "./constant";
import { useState, useEffect, useCallback } from "react";
const FILTER_STORAGE_KEY = "masterTableFilters";
const FILTER_LIST_STORAGE_KEY = "masterTableFilterList";

// Initialize default filter data
const getDefaultFilterData = () => ({
  ...advanceFilterData,
});

const getDefaultFilterList = () => ({
  ...advanceFilterList,
});

// localStorage utilities
export const filterStorageUtils = {
  // Get filter data from localStorage
  getFilterData: () => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Ensure all required properties exist
        return {
          ...getDefaultFilterData(),
          ...parsed,
        };
      }
      return getDefaultFilterData();
    } catch (error) {
      console.error("Error reading filter data from localStorage:", error);
      return getDefaultFilterData();
    }
  },

  // Save filter data to localStorage
  setFilterData: (data) => {
    try {
      // If data is a function, call it with current state
      if (typeof data === "function") {
        const currentData = filterStorageUtils.getFilterData();
        data = data(currentData);
      }
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(data));

      // Dispatch custom event to notify components
      window.dispatchEvent(
        new CustomEvent("filterDataChanged", {
          detail: data,
        })
      );
    } catch (error) {
      console.error("Error saving filter data to localStorage:", error);
    }
  },

  // Get filter list from localStorage
  getFilterList: () => {
    try {
      const stored = localStorage.getItem(FILTER_LIST_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...getDefaultFilterList(),
          ...parsed,
        };
      }
      return getDefaultFilterList();
    } catch (error) {
      console.error("Error reading filter list from localStorage:", error);
      return getDefaultFilterList();
    }
  },

  // Save filter list to localStorage
  setFilterList: (list) => {
    try {
      localStorage.setItem(FILTER_LIST_STORAGE_KEY, JSON.stringify(list));

      // Dispatch custom event to notify components
      window.dispatchEvent(
        new CustomEvent("filterListChanged", {
          detail: list,
        })
      );
    } catch (error) {
      console.error("Error saving filter list to localStorage:", error);
    }
  },

  // Reset filter data to defaults
  resetFilterData: () => {
    const defaultData = getDefaultFilterData();
    filterStorageUtils.setFilterData(defaultData);
    return defaultData;
  },

  // Clear all filter data
  clearFilterData: () => {
    try {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      localStorage.removeItem(FILTER_LIST_STORAGE_KEY);

      // Dispatch custom event to notify components
      window.dispatchEvent(new CustomEvent("filterDataCleared"));
    } catch (error) {
      console.error("Error clearing filter data:", error);
    }
  },

  // Check if any filters are active
  hasActiveFilters: () => {
    const filterData = filterStorageUtils.getFilterData();
    return Object.entries(filterData).some(
      ([key, value]) => value.status === true
    );
  },

  // Get active filter count
  getActiveFilterCount: () => {
    const filterData = filterStorageUtils.getFilterData();
    return Object.values(filterData).filter((filter) => filter.status === true)
      .length;
  },

  // Subscribe to filter changes
  subscribeToFilterChanges: (callback) => {
    const handleChange = (event) => {
      callback(event.detail);
    };

    window.addEventListener("filterDataChanged", handleChange);
    window.addEventListener("filterListChanged", handleChange);
    window.addEventListener("filterDataCleared", handleChange);

    // Return unsubscribe function
    return () => {
      window.removeEventListener("filterDataChanged", handleChange);
      window.removeEventListener("filterListChanged", handleChange);
      window.removeEventListener("filterDataCleared", handleChange);
    };
  },
};

// Hook for easier integration
export const useFilterStorage = () => {
  const [filterData, setFilterDataState] = useState(
    filterStorageUtils.getFilterData()
  );
  const [filterList, setFilterListState] = useState(
    filterStorageUtils.getFilterList()
  );

  useEffect(() => {
    // Subscribe to changes
    const unsubscribe = filterStorageUtils.subscribeToFilterChanges(() => {
      setFilterDataState(filterStorageUtils.getFilterData());
      setFilterListState(filterStorageUtils.getFilterList());
    });

    return unsubscribe;
  }, []);

  const updateFilterData = useCallback((data) => {
    filterStorageUtils.setFilterData(data);
  }, []);
  

  const updateFilterList = useCallback((list) => {
    filterStorageUtils.setFilterList(list);
  }, []);

  const resetFilters = useCallback(() => {
    filterStorageUtils.resetFilterData();
  }, []);

  return {
    filterData,
    filterList,
    updateFilterData,
    updateFilterList,
    resetFilters,
    hasActiveFilters: filterStorageUtils.hasActiveFilters(),
    activeFilterCount: filterStorageUtils.getActiveFilterCount(),
  };
};
